import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;

const ImageModal = ({ images, index, setIndex, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const current = images[index];

  const resetZoom = useCallback(() => {
    setZoom(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.5, MAX_ZOOM)), []);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const next = Math.max(z - 0.5, MIN_ZOOM);
      if (next <= 1) setTranslate({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const navigate = useCallback(
    (direction) => {
      setIndex((prev) => {
        let next = prev + direction;
        if (next < 0) next = images.length - 1;
        else if (next >= images.length) next = 0;
        return next;
      });
      resetZoom();
    },
    [images.length, setIndex, resetZoom],
  );

  useEffect(() => {
    const handleKeyboard = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") navigate(-1);
      else if (e.key === "ArrowRight") navigate(1);
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-" || e.key === "_") zoomOut();
      else if (e.key === "0") resetZoom();
    };

    document.addEventListener("keydown", handleKeyboard);
    return () => document.removeEventListener("keydown", handleKeyboard);
  }, [navigate, onClose, zoomIn, zoomOut, resetZoom]);

  const startDrag = (e) => {
    if (zoom <= 1) return;
    draggingRef.current = true;
    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
    startRef.current = { x: clientX - translate.x, y: clientY - translate.y };
  };

  const onDrag = (e) => {
    if (!draggingRef.current) return;
    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
    setTranslate({ x: clientX - startRef.current.x, y: clientY - startRef.current.y });
  };

  const endDrag = () => {
    draggingRef.current = false;
  };

  if (!current) return null;

  return createPortal(
    <div
      className="watermark-decoder__modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onMouseMove={onDrag}
      onMouseUp={endDrag}
      onTouchMove={onDrag}
      onTouchEnd={endDrag}>
      <div className="watermark-decoder__modal__content">
        <div className="watermark-decoder__modal__zoom-controls">
          <button type="button" onClick={zoomIn} title="Zoom In">
            +
          </button>
          <button type="button" onClick={zoomOut} title="Zoom Out">
            −
          </button>
          <button type="button" onClick={resetZoom} title="Reset Zoom">
            ⤢
          </button>
        </div>

        <div className="watermark-decoder__modal__controls">
          <button type="button" className="watermark-decoder__modal__close" onClick={onClose}>
            ✕
          </button>
        </div>

        {images.length > 1 && (
          <>
            <button
              type="button"
              className="watermark-decoder__modal__nav prev"
              onClick={() => navigate(-1)}>
              ‹
            </button>
            <button
              type="button"
              className="watermark-decoder__modal__nav next"
              onClick={() => navigate(1)}>
              ›
            </button>
          </>
        )}

        <div className="watermark-decoder__modal__image-container">
          <img
            src={current.url}
            alt={current.description}
            className="watermark-decoder__modal__image"
            style={{
              transform: `scale(${zoom}) translate(${translate.x / zoom}px, ${
                translate.y / zoom
              }px)`,
              cursor: zoom > 1 ? "move" : "default",
            }}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            draggable={false}
          />
        </div>
        <div className="watermark-decoder__modal__description">{current.description}</div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageModal;
